/**
 * Google Sheets URL 파싱 유틸리티
 *
 * 사용자가 입력한 Google Sheets URL 또는 ID에서
 * 스프레드시트 ID를 추출하고 검증합니다.
 *
 * @module spreadsheetUrlParser
 */

/**
 * Google Sheets URL 정규식 패턴
 *
 * 매칭 대상 URL 형식:
 * - https://docs.google.com/spreadsheets/d/{ID}/edit
 * - https://docs.google.com/spreadsheets/d/{ID}/edit#gid=0
 * - https://docs.google.com/spreadsheets/d/{ID}/edit?usp=sharing
 * - docs.google.com/spreadsheets/d/{ID}/edit (프로토콜 없음)
 * - 다양한 쿼리 파라미터 및 해시 포함
 *
 * 캡처 그룹 [1]: 스프레드시트 ID
 */
const SHEETS_URL_PATTERN =
  /(?:https?:\/\/)?(?:www\.)?docs\.google\.com\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/

/**
 * 스프레드시트 ID 형식 검증 정규식
 *
 * Google Sheets ID는 영문 대소문자, 숫자, 하이픈(-), 언더스코어(_)로 구성됩니다.
 */
const SPREADSHEET_ID_PATTERN = /^[a-zA-Z0-9_-]+$/

/**
 * 스프레드시트 ID 최소 길이
 *
 * Google Sheets ID는 일반적으로 44자이지만,
 * 안전성을 위해 최소 20자로 검증합니다.
 */
const MIN_ID_LENGTH = 20

/**
 * Google Sheets URL에서 스프레드시트 ID를 추출합니다.
 *
 * URL 형식인 경우 정규식을 사용하여 ID를 추출하고,
 * URL이 아닌 경우 입력값을 그대로 반환합니다(순수 ID로 간주).
 *
 * @param input - Google Sheets URL 또는 스프레드시트 ID
 * @returns 추출된 스프레드시트 ID (추출 실패 시 null)
 *
 * @example
 * ```typescript
 * extractSpreadsheetId('https://docs.google.com/spreadsheets/d/ABC123.../edit')
 * // => 'ABC123...'
 *
 * extractSpreadsheetId('ABC123...')
 * // => 'ABC123...'
 *
 * extractSpreadsheetId('')
 * // => null
 * ```
 */
export function extractSpreadsheetId(input: string): string | null {
  if (!input || typeof input !== 'string') {
    return null
  }

  // 1. URL 형식인 경우 정규식으로 ID 추출
  const match = input.match(SHEETS_URL_PATTERN)
  if (match && match[1]) {
    return match[1]
  }

  // 2. URL이 아니면 그대로 반환 (순수 ID로 간주)
  return input.trim()
}

/**
 * 스프레드시트 ID 형식이 유효한지 검증합니다.
 *
 * ID는 다음 조건을 만족해야 합니다:
 * - 최소 20자 이상
 * - 영문 대소문자, 숫자, 하이픈(-), 언더스코어(_)만 포함
 *
 * @param id - 검증할 스프레드시트 ID
 * @returns 유효하면 true, 아니면 false
 *
 * @example
 * ```typescript
 * isValidSpreadsheetId('1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms')
 * // => true
 *
 * isValidSpreadsheetId('short')
 * // => false (20자 미만)
 *
 * isValidSpreadsheetId('invalid@characters!')
 * // => false (특수 문자 포함)
 * ```
 */
export function isValidSpreadsheetId(id: string): boolean {
  if (!id || typeof id !== 'string') {
    return false
  }

  // 길이 및 패턴 검증
  return id.length >= MIN_ID_LENGTH && SPREADSHEET_ID_PATTERN.test(id)
}

/**
 * URL 또는 ID를 입력받아 유효한 스프레드시트 ID를 반환합니다.
 *
 * 이 함수는 extractSpreadsheetId와 isValidSpreadsheetId를 결합하여
 * 입력값을 파싱하고 검증하는 통합 함수입니다.
 *
 * @param input - Google Sheets URL 또는 스프레드시트 ID
 * @returns 유효한 스프레드시트 ID (실패 시 null)
 *
 * @example
 * ```typescript
 * // URL 입력
 * parseSpreadsheetInput('https://docs.google.com/spreadsheets/d/ABC123.../edit')
 * // => 'ABC123...'
 *
 * // gid 해시 포함
 * parseSpreadsheetInput('docs.google.com/spreadsheets/d/ABC123.../edit#gid=0')
 * // => 'ABC123...'
 *
 * // 쿼리 파라미터 포함
 * parseSpreadsheetInput('https://docs.google.com/spreadsheets/d/ABC123.../edit?usp=sharing')
 * // => 'ABC123...'
 *
 * // 순수 ID
 * parseSpreadsheetInput('1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms')
 * // => '1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms'
 *
 * // 잘못된 입력
 * parseSpreadsheetInput('invalid-url')
 * // => null
 * ```
 */
export function parseSpreadsheetInput(input: string): string | null {
  // 1. ID 추출
  const extractedId = extractSpreadsheetId(input)

  if (!extractedId) {
    return null
  }

  // 2. ID 형식 검증
  if (!isValidSpreadsheetId(extractedId)) {
    return null
  }

  return extractedId
}
